import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();
  const lastUpdated = 'April 16, 2026';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 transition-colors"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
              <img src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" alt="LikeKind" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {/* Title */}
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-8 h-8" style={{ color: 'var(--lk-sage)' }} />
          <h1 
            className="text-3xl sm:text-4xl font-bold" 
            style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit' }}
          >
            Terms of Service
          </h1>
        </div>
        <p className="mb-10" style={{ color: 'var(--fy-text-disabled)' }}>
          Last updated: {lastUpdated}
        </p>

        {/* Content */}
        <div className="space-y-10">
          
          {/* 1. Agreement */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              1. Agreement to These Terms
            </h2>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              These Terms form a legally binding agreement between you and LikeKind Inc., a Vermont corporation. By using LikeKind, you agree to these Terms and our{' '}
              <Link to="/privacy" className="underline" style={{ color: 'var(--lk-sage)' }}>Privacy Policy</Link>.
            </p>
          </section>

          {/* 2. What LikeKind Is */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              2. What LikeKind Is
            </h2>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is a coordination platform designed to support real-world activity for individuals, families, schools, and organizations.
            </p>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind includes limited, structured communication features only where necessary for coordination. Most coordination occurs through participation signals and activity-based interaction, not open messaging systems.
            </p>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              <strong>Coordination disclaimer:</strong> Communication features are designed for coordination and information sharing. LikeKind is not an emergency response service and should not be relied upon as the sole method for critical notifications.
            </p>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              Participation data may reflect a user's involvement in activities and may be visible based on selected visibility settings. This data is used only to support coordination and context within the platform.
            </p>
            <p className="leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is designed to reduce digital pressure such as ranking, engagement scoring, and algorithmic amplification. We do not guarantee emotional, behavioral, or social outcomes.
            </p>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is designed around real-world activity coordination rather than content sharing or social performance.
            </p>
          </section>

          {/* 3. Eligibility */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              3. Eligibility and Account Types
            </h2>
            
            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              Adults
            </h3>
            <p className="mb-4 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              You must be 18 or older to create an independent adult account.
            </p>

            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              Family Accounts and Minors
            </h3>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Minors may use LikeKind only through:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>A family account created and managed by a parent or guardian (18+)</li>
              <li>A school or organizational program administered by authorized adults</li>
            </ul>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Children cannot create independent accounts.
            </p>
          </section>

          {/* 4. Parent Responsibilities */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              4. Parent or Guardian Responsibilities
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              If you manage a minor account, you agree that:
            </p>
            <ul className="list-disc list-inside space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>You are the parent or legal guardian (or have lawful authority)</li>
              <li>You consent to the data processing necessary to operate the minor account</li>
              <li>You will maintain oversight through LikeKind's controls</li>
              <li>You will not attempt to disable or bypass minor safety protections</li>
            </ul>
          </section>

          {/* 5. Safety Rules for Minors */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              5. Safety Rules for Minors
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              For minors:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>Children under 13 have no public discoverability</li>
              <li>Teen discoverability is limited and controlled by parent settings</li>
              <li>Child-to-child direct messaging is disabled and cannot be enabled</li>
              <li>Certain uploads may require parent approval</li>
              <li>Sponsors cannot target minors</li>
            </ul>
            
            {/* Cross-link to Parent Visibility */}
            <p className="mb-4 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              For complete details on visibility modes and child protections, see{' '}
              <Link to="/parent-visibility" className="underline font-medium" style={{ color: 'var(--lk-sage)' }}>
                Parent Visibility & Child Safety
              </Link>.
            </p>

            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Child safety violations may result in immediate permanent removal of accounts and access.
            </p>
          </section>

          {/* 6. Your Content */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              6. Your Content and License
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              You retain ownership of your content.
            </p>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              By posting, you grant LikeKind a limited, non-exclusive, worldwide license to host and display your content within the platform solely to operate the service.
            </p>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              We do not sell your content or use it for behavioral advertising.
            </p>
          </section>

          {/* 7. Acceptable Use */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              7. Acceptable Use
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              You agree not to:
            </p>
            <ul className="list-disc list-inside space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>Harass, threaten, exploit, or endanger others</li>
              <li>Post illegal or rights-violating content</li>
              <li>Impersonate others</li>
              <li>Attempt to bypass security or parental controls</li>
              <li>Use bots or scraping tools</li>
              <li>Upload malicious software</li>
              <li>Attempt to collect or contact minors outside permitted flows</li>
            </ul>
          </section>

          {/* 8. Sponsorship */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              8. Sponsorship
            </h2>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind may display clearly labeled sponsorship placements. Sponsors do not receive behavioral user data.
            </p>
          </section>

          {/* 9. School and Organization Features */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              9. School and Organization Features
            </h2>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              If used by schools or organizations, structured coordination tools such as announcements operate within the role-based safety controls defined by account type.
            </p>
          </section>

          {/* 10. Enforcement */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              10. Enforcement
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              We may remove content or restrict accounts that violate these Terms or create safety risk. Enforcement may involve automated detection and manual review.
            </p>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              For child safety violations, action may be immediate and permanent.
            </p>
          </section>

          {/* 11. Termination */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              11. Termination
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              You may stop using LikeKind at any time.
            </p>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              We may suspend or terminate accounts for violations or safety risk.
            </p>
          </section>

          {/* 12. Disclaimers */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              12. Disclaimers
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is provided "as is" and "as available."
            </p>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind is not an emergency response service and should not be relied upon as the sole method of delivering critical emergency information. Delivery may depend on third-party services.
            </p>
          </section>

          {/* 13. Limitation of Liability */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              13. Limitation of Liability
            </h2>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              To the maximum extent permitted by law, LikeKind Inc. will not be liable for indirect, incidental, consequential, special, or punitive damages arising from use of the service.
            </p>
          </section>

          {/* 14. Governing Law */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              14. Governing Law
            </h2>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              These Terms are governed by the laws of the State of Vermont.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Contact
            </h2>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Support: <a href="https://likekind.com/support" className="underline" style={{ color: 'var(--lk-sage)' }}>likekind.com/support</a>
            </p>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Privacy: <a href="mailto:privacy@likekind.com" className="underline" style={{ color: 'var(--lk-sage)' }}>privacy@likekind.com</a>
            </p>
          </section>

          {/* Related Links */}
          <section className="pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Related
            </h3>
            <div className="flex flex-wrap gap-3">
              <Link 
                to="/privacy" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Privacy Policy
              </Link>
              <Link 
                to="/parent-visibility" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Parent Visibility
              </Link>
              <Link 
                to="/founder-statement" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Founder Statement
              </Link>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p style={{ color: 'var(--fy-text-disabled)' }}>
            © {new Date().getFullYear()} LikeKind Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
